import { skipToken, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { memo, type ReactNode, useEffect, useMemo, useState } from 'react'

import { type OperatingZone } from '@orus.eu/operating-zone'

import { useTRPC } from '../../client.ts'
import {
  getInitialOperatingZoneFromUrl,
  OperatingZoneContext,
  useSetOperatingZone,
} from '../../lib/operating-zone-context.ts'
import { useSession } from '../../lib/session.ts'

const rootRoute = getRouteApi('__root__')

export const OperatingZoneWrapper = memo<{ children: ReactNode }>(function OperatingZoneWrapper({ children }) {
  const trpc = useTRPC()
  const session = useSession()
  const initialOperatingZone = useMemo(() => getInitialOperatingZoneFromUrl(), [])
  const [operatingZoneOverride, setOperatingZone] = useState<OperatingZone | null>(null)

  const hasPermissions =
    session?.permissions.type === 'client' &&
    session.permissions.rolesPermissions.flatMap((role) => role.permissions).includes('contract.read')

  const { data: contracts } = useQuery(
    trpc.contracts.listMyContracts.queryOptions(hasPermissions ? undefined : skipToken),
  )

  const contractOperatingZone = hasPermissions ? contracts?.[0]?.operatingZone : undefined
  const operatingZone = operatingZoneOverride ?? contractOperatingZone ?? initialOperatingZone

  const operatingZoneContextValue = useMemo(
    () => ({ operatingZone, setOperatingZone }),
    [operatingZone, setOperatingZone],
  )

  return <OperatingZoneContext.Provider value={operatingZoneContextValue}>{children}</OperatingZoneContext.Provider>
})

export const OperatingZoneSync = memo(function OperatingZoneSync() {
  const { operating_zone: operatingZoneFromUrl } = rootRoute.useSearch()
  const setOperatingZone = useSetOperatingZone()

  useEffect(
    function synchronizeOperatingZoneFromUrl() {
      if (operatingZoneFromUrl) {
        setOperatingZone(operatingZoneFromUrl)
      }
    },
    [operatingZoneFromUrl, setOperatingZone],
  )

  return null
})
